import { message } from 'antd';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { DURATION_TIMER } from 'appConstants';
import { makeSelectAuthUser } from 'containers/App/selectors';

import {
  createBDOSettingFail,
  createBDOSettingSuccess,
  deleteBDOSettingFail,
  deleteBDOSettingSuccess,
  getBDOSetting,
  getBDOSettingFail,
  getBDOSettings,
  getBDOSettingsFail,
  getBDOSettingsSuccess,
  getBDOSettingSuccess,
  updateBDOSettingFail,
  updateBDOSettingSuccess
} from './action';
import {
  createBdoSettingAPI,
  deleteBdoSettingAPI,
  getBDOSettingAPI,
  getBDOSettingsAPI,
  updateBdoSettingAPI
} from './api';
import {
  CREATE_BDO_SETTING,
  DELETE_BDO_SETTING,
  GET_BDO_SETTING,
  GET_BDO_SETTINGS,
  UPDATE_BDO_SETTING
} from './constants';
import { makeSelectBDOSettingParams } from './selectors';

function* getBDOSettingsSaga(action) {
  try {
    const authUser = yield select(makeSelectAuthUser());
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authUser.bearer_token}`
    };
    const response = yield call(getBDOSettingsAPI, action.payload, headers);
    if (response.error) {
      yield put(getBDOSettingsFail(response.error));
      message.error(response.error.message, DURATION_TIMER);
    } else {
      yield put(getBDOSettingsSuccess(response));
    }
  } catch (error) {
    yield put(getBDOSettingsFail(error));
    message.error(error.message, DURATION_TIMER);
  }
}

function* getBDOSettingSaga(action) {
  const authUser = yield select(makeSelectAuthUser());

  let headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authUser.bearer_token}`
  };

  try {
    const response = yield call(getBDOSettingAPI, action.payload, headers);
    yield put(getBDOSettingSuccess(response.data.data));
  } catch (e) {
    if (e.data.message) {
      message.error(e.data.message);
    } else {
      message.error('Failed to load Card Item');
    }
    yield put(getBDOSettingFail(e.data));
  }
}

function* createBdoSettingSaga(action) {
  const { payload, onSuccess, onError } = action;
  try {
    const authUser = yield select(makeSelectAuthUser());
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authUser.bearer_token}`
    };
    const response = yield call(createBdoSettingAPI, payload, headers);
    if (response.status !== 201) {
      yield put(createBDOSettingFail(response));
      message.error(response.data.message, DURATION_TIMER);
      if (onError) onError(response);
    } else {
      yield put(createBDOSettingSuccess(response.data));
      message.success(response.data.message || 'Card item created successfully', DURATION_TIMER);
      const params = yield select(makeSelectBDOSettingParams());
      const paramsToUse = {
        ...params,
        type: action.payload.item_type
      };
      yield put(getBDOSettings(paramsToUse));
      if (onSuccess) onSuccess(response);
    }
  } catch (error) {
    yield put(createBDOSettingFail(error));
    message.error(error.message, DURATION_TIMER);
    if (onError) onError(error);
  }
}

function* updateBdoSettingSaga(action) {
  const authUser = yield select(makeSelectAuthUser());
  const { payload, onSuccess, onError } = action;
  let headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authUser.bearer_token}`
  };

  try {
    const { id, ...body } = payload;
    const response = yield call(updateBdoSettingAPI, body, { id }, headers);

    if (response.status !== 200) {
      yield put(updateBDOSettingFail(response));
      message.error(response.data.message, DURATION_TIMER);
      if (onError) onError(response.data);
    } else {
      yield put(updateBDOSettingSuccess(response.data));
      message.success(response.data.message || 'Card item updated successfully', DURATION_TIMER);
      const params = yield select(makeSelectBDOSettingParams());
      const paramsToUse = {
        ...params,
        type: action.payload.item_type
      };
      yield put(getBDOSettings(paramsToUse));
      yield put(getBDOSetting(id));
      if (onSuccess) onSuccess(response.data);
    }
  } catch (e) {
    yield put(updateBDOSettingFail(e));
    message.error(e.data.message, DURATION_TIMER);
    if (onError) onError(e.data);
  }
}

function* deleteBdoSettingSaga(action) {
  const authUser = yield select(makeSelectAuthUser());

  let headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authUser.bearer_token}`
  };

  try {
    yield call(deleteBdoSettingAPI, action.payload, headers);
    yield put(deleteBDOSettingSuccess(action.payload));
    message.success('Card Item deleted successfully', DURATION_TIMER);
  } catch (e) {
    yield put(deleteBDOSettingFail(e.response || e));
    message.error(e.response?.data?.message || 'Failed to delete Card Item', DURATION_TIMER);
  }
}

function* BDOSettingsSaga() {
  yield takeLatest(GET_BDO_SETTING, getBDOSettingSaga);
  yield takeLatest(GET_BDO_SETTINGS, getBDOSettingsSaga);
  yield takeLatest(UPDATE_BDO_SETTING, updateBdoSettingSaga);
  yield takeLatest(DELETE_BDO_SETTING, deleteBdoSettingSaga);
  yield takeLatest(CREATE_BDO_SETTING, createBdoSettingSaga);
}

export default BDOSettingsSaga;
